/**
 * Step 4: Exclusion Map (Optional)
 *
 * Interactive map for excluding specific features from the quiz.
 * Click regions on the map to toggle their exclusion.
 */

import { useMemo } from "react";
import { HelpCircle, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  useStudioStore,
  selectDataset,
  selectFilters,
  toggleFeatureExclusion,
} from "@/stores/useStudioStore";
import { ExclusionMap } from "@/components/studio/ExclusionMap";
import { MapCredits } from "@/components/shared/MapCredits";
import { geojsonDatasets } from "@/data/geojsonDatasets";
import { applyFilters } from "@/utils/filterUtils";
import { getFeatureIdentifier } from "@/utils/geojsonLoader";

export function Step4ExclusionMap() {
  const dataset = useStudioStore(selectDataset);
  const filters = useStudioStore(selectFilters);
  const excludedFeatureIds = useStudioStore((state) => state.excludedFeatureIds);
  const promptKeys = useStudioStore((state) => state.promptKeys);

  if (!dataset.collection) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          <p>No dataset loaded. Please go back to Step 1.</p>
        </CardContent>
      </Card>
    );
  }

  // Apply filters to get the collection that will be shown on the map (memoized)
  const filteredCollection = useMemo(
    () => applyFilters(dataset.collection!, filters),
    [dataset.collection, filters]
  );

  // Get feature names for excluded features (with proper ID matching) (memoized)
  // IMPORTANT: Use original unfiltered collection to match IDs correctly
  const excludedFeatures = useMemo(() => {
    if (!dataset.collection) return [];

    return dataset.collection.features.filter((feature, originalIndex) => {
      const featureId = getFeatureIdentifier(feature, originalIndex);
      return excludedFeatureIds.includes(featureId);
    });
  }, [dataset.collection, excludedFeatureIds]);

  const handleClearAll = () => {
    excludedFeatureIds.forEach((id) => toggleFeatureExclusion(id));
  };

  // Handle exclusion toggle: when a merged feature is clicked,
  // we need to toggle ALL original features with matching prompt values
  const handleToggleExclusion = (promptValue: string) => {
    if (promptKeys.length === 0 || !dataset.collection) return;

    // IMPORTANT: Search in the ORIGINAL unfiltered collection to get correct indices
    // Using filtered collection indices causes ID mismatches!
    const matchingFeatureIds: string[] = [];
    dataset.collection.features.forEach((feature, originalIndex) => {
      if (!feature.properties) return;

      // Build prompt value for this feature
      const featurePromptValue = promptKeys
        .map((key) => {
          const value = feature.properties?.[key];
          return Array.isArray(value)
            ? JSON.stringify([...value].sort())
            : JSON.stringify(value);
        })
        .join("||");

      if (featurePromptValue === promptValue) {
        // Use the ORIGINAL index from the unfiltered collection
        const featureId = getFeatureIdentifier(feature, originalIndex);
        matchingFeatureIds.push(featureId);
      }
    });

    // Toggle all matching features
    // Check if all are excluded -> if so, include them all; otherwise exclude them all
    const allExcluded = matchingFeatureIds.every((id) => excludedFeatureIds.includes(id));

    if (allExcluded) {
      // Include all
      matchingFeatureIds.forEach((id) => {
        if (excludedFeatureIds.includes(id)) {
          toggleFeatureExclusion(id);
        }
      });
    } else {
      // Exclude all
      matchingFeatureIds.forEach((id) => {
        if (!excludedFeatureIds.includes(id)) {
          toggleFeatureExclusion(id);
        }
      });
    }
  };

  // Find dataset credits if using catalogue dataset
  const catalogueDataset = dataset.source?.type === "catalogue" && dataset.source?.id
    ? geojsonDatasets.find(d => d.id === dataset.source?.id)
    : null;

  return (
    <div className="space-y-6">
      {/* Instructions Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Exclude Regions (Optional)</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Click regions on the map to exclude them from the quiz
              </p>
            </div>
            <Badge variant="outline">Optional</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Info box */}
          <div className="p-4 rounded-lg bg-muted/50 border">
            <div className="flex items-start gap-3">
              <HelpCircle className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">
                  How exclusions work
                </p>
                <p>
                  Click any region on the map below to exclude it. Excluded regions
                  appear grayed out and won't be included in your quiz. Click again to
                  include them back.
                </p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-between text-sm">
            <div>
              <span className="text-muted-foreground">Total features: </span>
              <span className="font-medium">{filteredCollection.features.length}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Excluded: </span>
              <span className="font-medium">{excludedFeatureIds.length}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Included: </span>
              <span className="font-medium text-primary">
                {filteredCollection.features.length - excludedFeatureIds.length}
              </span>
            </div>
          </div>

        </CardContent>
      </Card>

      {/* Map Card */}
      <Card>
        <CardContent className="p-0">
          <div className="h-[500px] rounded-lg overflow-hidden">
            <ExclusionMap
              collection={filteredCollection}
              originalCollection={dataset.collection}
              excludedFeatureIds={excludedFeatureIds}
              promptKeys={promptKeys}
              onToggleExclusion={handleToggleExclusion}
            />
          </div>
          {/* Map Credits */}
          {catalogueDataset?.credits && catalogueDataset.credits.length > 0 && (
            <MapCredits variant="inline" credits={catalogueDataset.credits} />
          )}
        </CardContent>
      </Card>

      {/* Excluded features list */}
      {excludedFeatures.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Excluded Features</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                className="text-xs h-7"
              >
                Clear All
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
              {excludedFeatures.map((feature) => {
                // Recalculate the original index from the unfiltered collection
                const originalIndex = dataset.collection!.features.indexOf(feature);
                const featureId = getFeatureIdentifier(feature, originalIndex);

                // Try to get a human-readable name
                let featureName = "";

                // First try common name properties
                if (feature.properties?.Name || feature.properties?.name) {
                  featureName = feature.properties.Name || feature.properties.name;
                }
                // Then try prompt keys (the properties being quizzed on)
                else if (promptKeys.length > 0) {
                  const values = promptKeys
                    .map((key) => feature.properties?.[key])
                    .filter((val) => val !== null && val !== undefined && val !== "")
                    .map((val) => Array.isArray(val) ? val.join(", ") : String(val));

                  if (values.length > 0) {
                    featureName = values.join(" · ");
                  }
                }

                // Final fallback to feature ID
                if (!featureName) {
                  featureName = featureId;
                }

                return (
                  <Badge
                    key={featureId}
                    variant="secondary"
                    className="gap-1 pr-1"
                  >
                    {featureName}
                    <button
                      className="ml-1 rounded-full hover:bg-muted p-0.5"
                      onClick={() => toggleFeatureExclusion(featureId)}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-blue-500 opacity-50 border-2 border-white"></div>
              <span className="text-muted-foreground">Included</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gray-400 opacity-30 border-2 border-white"></div>
              <span className="text-muted-foreground">Excluded</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
